<template>
    <div>
        <div class="box view-nav-bar">
            <div class="d-flex align-items-center w-100">
                <div class="flex-grow-1">
                    <router-link :to="'/admissions/cinema/' + theatreCode" class="mr-3">
                        <icon name="chevron-left"/> Back
                    </router-link>
                    <strong>{{ theatreCode }}</strong>
                    <span v-if="bucket.theatre_name" class="ml-2">{{ bucket.theatre_name }}<span v-if="bucket.city">, {{ bucket.city }}</span></span>
                    <span class="text-muted ml-2">— cinema year <strong>{{ year }}</strong>, all sessions vs. Brightfish preshow</span>
                </div>
            </div>
        </div>

        <div v-if="loading" class="box">
            <div class="box-content text-center text-muted py-4">Loading…</div>
        </div>

        <div v-else-if="!totals.weeks" class="box">
            <div class="box-content text-center text-muted py-4">No reported admissions for {{ theatreCode }} in {{ year }}.</div>
        </div>

        <template v-else>
            <div class="box">
                <div class="box-content">
                    <table class="shx-table w-100 mt-2">
                        <thead>
                            <tr>
                                <th></th>
                                <th class="text-right">Admissions</th>
                                <th class="text-right shx-th-120">Avg / week</th>
                                <th class="text-right shx-th-120">% of all</th>
                                <th class="text-right shx-th-100">Real %</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>
                                    <strong>All sessions</strong>
                                    <span class="text-muted small ml-2">every title, every sales house</span>
                                </td>
                                <td class="text-right">{{ formatInt(totals.admissions) }}</td>
                                <td class="text-right">{{ formatInt(totals.avg_week) }}</td>
                                <td class="text-right">100%</td>
                                <td class="text-right">
                                    <span>{{ totals.real_weighted != null ? totals.real_weighted + '%' : '—' }}</span>
                                    <span v-if="totals.real_weighted != null && totals.real_weighted <= 90"
                                          class="badge badge-warning ml-1"
                                          :title="'Minimum real: ' + totals.real_min + '%'">⚠</span>
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    <strong>Preshow sessions</strong>
                                    <span class="text-muted small ml-2">sales house BF</span>
                                </td>
                                <td class="text-right">{{ formatInt(totals.admissions_preshow) }}</td>
                                <td class="text-right">{{ formatInt(totals.avg_week_preshow) }}</td>
                                <td class="text-right">{{ totals.pct_preshow !== null ? totals.pct_preshow + '%' : '—' }}</td>
                                <td></td>
                            </tr>
                        </tbody>
                    </table>
                    <p class="text-muted small mt-2 mb-0">
                        Averages over <strong>{{ totals.weeks }}</strong> week<span v-if="totals.weeks !== 1">s</span>
                        of <code>admissions_week</code>, compiled and estimated alike. Preshow = rows with
                        <code>saleshouse = BF</code>; all sessions = no filter on sales house or Movix id.
                        <strong>Real %</strong> is the admissions-weighted average of <code>real</code>:
                        97.5 = fully compiled from Cinedata, lower = partly estimated.
                    </p>
                </div>
            </div>

            <div class="box">
                <div class="box-content">
                    <heading upper="Top movies" :lower="'Top ' + movies.length + ' by admissions in ' + year + ', all sales houses'" border/>
                    <table class="shx-table w-100 mt-2">
                        <thead>
                            <tr>
                                <th class="shx-th-50">#</th>
                                <th class="shx-th-80">Movix</th>
                                <th>Title</th>
                                <th class="text-right shx-th-100"># Weeks</th>
                                <th class="text-right">Admissions</th>
                                <th class="text-right shx-th-100">% of all</th>
                                <th class="text-right shx-th-120">Preshow (BF)</th>
                                <th class="text-right shx-th-120">% of preshow</th>
                                <th class="text-right shx-th-100">Real %</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(movie, index) in movies" :key="movie.movix_id">
                                <td class="text-muted">{{ index + 1 }}</td>
                                <td>
                                    <router-link :to="'/history/' + movie.movix_id">{{ movie.movix_id }}</router-link>
                                </td>
                                <td>{{ movie.title || '—' }}</td>
                                <td class="text-right">{{ movie.weeks }}</td>
                                <td class="text-right">{{ formatInt(movie.admissions) }}</td>
                                <td class="text-right">{{ movie.pct !== null ? movie.pct + '%' : '—' }}</td>
                                <td class="text-right">{{ formatInt(movie.admissions_preshow) }}</td>
                                <td class="text-right">{{ movie.pct_preshow !== null ? movie.pct_preshow + '%' : '—' }}</td>
                                <td class="text-right">
                                    <span>{{ movie.real_weighted != null ? movie.real_weighted + '%' : '—' }}</span>
                                    <span v-if="movie.real_weighted != null && movie.real_weighted <= 90"
                                          class="badge badge-warning ml-1">⚠</span>
                                </td>
                            </tr>
                        </tbody>
                        <tfoot>
                            <tr>
                                <td colspan="4"><strong>Top {{ movies.length }}</strong></td>
                                <td class="text-right"><strong>{{ formatInt(topAdmissions) }}</strong></td>
                                <td class="text-right"><strong>{{ pct(topAdmissions, totals.admissions) }}</strong></td>
                                <td class="text-right"><strong>{{ formatInt(topPreshow) }}</strong></td>
                                <td class="text-right"><strong>{{ pct(topPreshow, totals.admissions_preshow) }}</strong></td>
                                <td></td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
import axios from 'axios';
import Heading from '../../partials/Heading';

export default {
    name: 'AdmissionsCinemaYear',
    components: {
        Heading,
    },
    props: ['theatreCode', 'year'],
    data() {
        return {
            loading: true,
            bucket: {},
            totals: {},
            movies: [],
        };
    },
    computed: {
        topAdmissions() {
            return this.movies.reduce((sum, m) => sum + (m.admissions || 0), 0);
        },
        topPreshow() {
            return this.movies.reduce((sum, m) => sum + (m.admissions_preshow || 0), 0);
        },
    },
    created() {
        this.loadYear();
    },
    watch: {
        theatreCode() {
            this.loadYear();
        },
        year() {
            this.loadYear();
        },
    },
    methods: {
        loadYear() {
            this.loading = true;
            axios
                .get(`/admissions/cinema/${this.theatreCode}/${this.year}`)
                .then(response => {
                    const data = response.data.data;
                    this.bucket = data.bucket || {};
                    this.totals = data.totals || {};
                    this.movies = data.movies || [];
                })
                .catch(error => {
                    this.$toast.error(error.response?.data?.error?.message || error.message);
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        pct(part, total) {
            return total ? (Math.round(part / total * 1000) / 10) + '%' : '—';
        },
        formatInt(v) {
            return Math.round(v || 0).toLocaleString();
        },
    },
};
</script>
